import {
  ADMIN_ORDER_DETAILS,
  ADMIN_SUPPORT_TICKETS,
  AdminHeader,
  archiveConversation,
  ChatConfirmModal,
  ChatThread,
  Div,
  isGroupConversation,
  isPublicConversation,
  reportError,
  SuccessToast,
  SUPPORT_TICKET_PARAM,
  useAuthenticatedAuthUser,
  usePermissions,
  UserPermission,
  UserRole,
  useVisualViewportHeight,
} from '@vgrubs/components';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ChatCard, PageTopGap, ThreadArea } from './Chat.styled';
import { ChatInboxPanel } from './ChatInboxPanel';
import { ChatThreadHeader } from './ChatThreadHeader';
import { ConversationDetailsModal } from './ConversationDetailsModal';
import { NewConversationModal } from './NewConversationModal';
import { PublicThreadHeader } from './PublicThreadHeader';
import { openUserDetails } from './userDetailsPath';
import { ViktorThreadHeader } from './ViktorThreadHeader';
import { useChatFilters } from './hooks/useChatFilters';
import { useChatInbox } from './hooks/useChatInbox';
import {
  useConversationDetail,
  useViktorConversation,
} from './hooks/useConversationQueries';

/**
 * The admin chat page: an inbox on the left, the open conversation on the
 * right, and the two modals that manage them.
 *
 * Both panes live in one card - see `Chat.styled` for why - and below 1200px
 * only one of them is mounted at a time, which is what the `hidden` flags say.
 */
export const Chat = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const user = useAuthenticatedAuthUser();
  useVisualViewportHeight();

  /*
   * Viktor is by role, not by permission: `conversationRead` and
   * `conversationWrite` gate the support queue, and the backend ignores both
   * here. Marketing has no Viktor at all - the route answers 403 - so the card
   * is not drawn for them rather than drawn and broken.
   */
  const canUseViktor =
    user.role === UserRole.admin || user.role === UserRole.support;

  const controls = useChatFilters();
  const { filters, selectedId, selectThread } = controls;

  const [newOpen, setNewOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  /** Armed by the header's Archive; the request waits for the confirmation. */
  const [archiving, setArchiving] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);

  // The inbox column is its own scroll container above 1200px, so the paging
  // sentinel has to be observed against it rather than the viewport.
  const inboxRef = useRef<HTMLDivElement | null>(null);
  const inbox = useChatInbox(filters, inboxRef);

  /*
   * The viewer's one thread with Viktor, fetched (by an idempotent POST) as
   * the page opens - which is how its id is known even while it is empty and
   * therefore in no list. The card above the inbox is its row.
   */
  const viktor = useViktorConversation({ enabled: canUseViktor });

  /* The Viktor thread is in no list, so its row comes from its own query. */
  const selectedThread =
    viktor.data && viktor.data.id === selectedId
      ? viktor.data
      : inbox.threads.find((thread) => thread.id === selectedId);

  const viktorSelected = selectedThread?.type === 'viktor';
  /* A customer from a tracking link or a website visitor: the AI answers
     first, and the header decides who has the thread. */
  const publicThread =
    selectedThread && isPublicConversation(selectedThread.type)
      ? selectedThread
      : null;

  /**
   * Members and the group title, for the `@` menu and the header count.
   *
   * Only fetched for a group of either kind: a support or direct thread has
   * nobody to mention, and its name is already on the inbox row.
   */
  const { data: detail } = useConversationDetail({
    conversationId: selectedId,
    enabled:
      isGroupConversation(selectedThread?.type) ||
      /* And whenever the inbox has no row for the open thread. A conversation
         with nothing in it yet is not in the list at all - starting one from
         the "+" makes exactly that - so without this the chat you just opened
         has no name, and the thread would lay itself out as a support one. */
      (selectedId !== null && !selectedThread),
  });

  /** What to call a thread the inbox has no row for - see above. */
  const detailName =
    detail?.title ||
    detail?.participants
      .filter((participant) => participant.userId !== user.id)
      .map((participant) => participant.name)
      .join(', ');

  /**
   * Write access is per conversation, not per page.
   *
   * `conversationWrite` gates SUPPORT threads - answering a merchant is the
   * thing being delegated. A group or a direct message you are already in needs
   * no permission: being in it is the permission - a merchant group included,
   * which only its members ever see. Neither does Viktor.
   */
  const canWrite =
    selectedThread?.type === 'support' || publicThread
      ? can(UserPermission.conversationWrite)
      : true;

  /**
   * Whether the "+" has anything to offer on this tab.
   *
   * Support is the one kind that needs a permission - answering an outside
   * user is the thing being delegated - so without it the Sales tab would open
   * a modal with nothing in it. Starting a conversation with a colleague needs
   * nothing, and neither does a merchant group: making one is the admin and
   * support roles' job whatever their permissions, which is why the Merchants
   * tab offers the "+" to those two either way.
   */
  const canOpenSupport = can(UserPermission.conversationWrite);
  const canCreateGroups =
    user.role === UserRole.admin || user.role === UserRole.support;
  /* Never on the Dispatcher tab: only a customer opens one of those, from
     the tracking link. */
  const canStartConversation =
    filters.scope !== 'dispatcher' &&
    (filters.scope === 'internal' ||
      canOpenSupport ||
      (filters.scope === 'merchant' && canCreateGroups));

  /*
   * The app's own page header, on a phone and only over the list.
   *
   * A desktop does not get one: the two panes already fill the screen and a bar
   * repeating the word the sidebar highlights would cost 75px of conversation.
   * On a phone the inbox is a screen like any other and is expected to carry
   * the same header - which is also where the sidebar button belongs, so the
   * tabs below no longer keep a copy of it.
   *
   * Not over an open thread, though: that one has its own header with the way
   * back in it, and two stacked bars on a phone leave the conversation reading
   * through a letterbox.
   */
  const showPageHeader = selectedId === null;

  /*
   * On or off the shelf, from the row when the inbox has one and from the
   * conversation itself when it does not - which is the case the moment you
   * archive a thread while looking at it, since the list it was in no longer
   * contains it.
   */
  const threadArchived = selectedThread?.archived ?? detail?.archived ?? false;

  /**
   * Archive is confirmed rather than immediate.
   *
   * It is reversible and destroys nothing, but it takes a conversation out of
   * the list a team works from - and the control sits one gap away from
   * Details, in a header that is clicked all day.
   *
   * Closing the thread afterwards is the honest end of it: the row has moved to
   * the other list, and leaving it open beside a list that no longer holds it
   * is the same mismatch the new-conversation flow already avoids.
   */
  const toggleArchive = async () => {
    if (selectedId === null) return;
    try {
      setArchiveBusy(true);
      await archiveConversation(selectedId, !threadArchived);
      SuccessToast(threadArchived ? 'Moved back to the inbox.' : 'Archived.');
      setArchiving(false);
      selectThread(null);
      inbox.refresh();
    } catch (error) {
      reportError(error);
    } finally {
      setArchiveBusy(false);
    }
  };

  return (
    <>
      {showPageHeader && (
        <Div $mobile>
          <AdminHeader title="Chat" />
        </Div>
      )}

      <PageTopGap />

      <ChatCard $withHeader={showPageHeader}>
        <ChatInboxPanel
          controls={controls}
          inbox={inbox}
          listRef={inboxRef}
          hidden={selectedId !== null}
          onNewConversation={() => setNewOpen(true)}
          canCreate={canStartConversation}
          canUseViktor={canUseViktor}
          viktorThread={viktor.data}
        />

        <ThreadArea $hiddenOnMobile={selectedId === null}>
          <ChatThread
            embedded
            bare
            header={
              selectedId !== null &&
              (viktorSelected ? (
                <ViktorThreadHeader onBack={() => selectThread(null)} />
              ) : publicThread ? (
                <PublicThreadHeader
                  thread={publicThread}
                  onBack={() => selectThread(null)}
                />
              ) : (
                <ChatThreadHeader
                  thread={selectedThread}
                  fallbackName={detailName}
                  /* From the detail query, not the inbox row's flat
                     `participants` string: this one is the full list and the
                     socket keeps it current as people join and leave. */
                  memberNames={detail?.participants.map(
                    (participant) => participant.name
                  )}
                  archived={threadArchived}
                  onBack={() => selectThread(null)}
                  onOpenDetails={() => setDetailsOpen(true)}
                  onToggleArchive={() => setArchiving(true)}
                />
              ))
            }
            conversationId={selectedId}
            conversationType={selectedThread?.type ?? detail?.type ?? 'support'}
            viewerId={user.id}
            canWrite={canWrite}
            /* The row's count as of the click - the thread freezes it before
               its own markRead zeroes the row, and opens on the "New messages"
               divider it points at. */
            unreadCount={selectedThread?.unread ?? 0}
            currentSender={{
              id: user.id,
              name: user.name || `${user.firstName} ${user.lastName}`,
              role: user.role,
            }}
            fallbackSenderName={selectedThread?.name ?? detailName}
            emptyStateTitle={viktorSelected ? 'Ask vGrubs AI' : undefined}
            emptyState={
              viktorSelected
                ? 'vGrubs AI is connected to every vGrubs system. Ask about locations, merchants, orders, payouts or anything else, in any language.'
                : undefined
            }
            /* Everyone, yourself included - the thread drops you from the `@`
               menu itself and needs the full list to highlight the name in a
               message that was addressed to you. */
            members={detail?.participants.map((participant) => ({
              id: participant.userId,
              name: participant.name,
              role: participant.role,
            }))}
            onOpenOrder={(orderId) =>
              navigate(ADMIN_ORDER_DETAILS.replace(':id', String(orderId)))
            }
            /* The card in the thread is read-only; this is the way from what
               the bot said to the queue the team works it from - a new tab,
               for the same reason the name under a bubble opens one. */
            onOpenTicket={(ticketId) =>
              window.open(
                `${ADMIN_SUPPORT_TICKETS}?${SUPPORT_TICKET_PARAM}=${ticketId}`,
                '_blank'
              )
            }
            /* The name under a bubble is the same link the thread header and
               the member list use - a new tab, so the conversation you are
               reading stays where it is. */
            onOpenUser={openUserDetails}
          />
        </ThreadArea>
      </ChatCard>

      <NewConversationModal
        isOpen={newOpen}
        onClose={() => setNewOpen(false)}
        scope={filters.scope}
        canOpenSupport={canOpenSupport}
        canCreateGroups={canCreateGroups}
        onOpened={(conversationId) => {
          inbox.refresh();
          selectThread(conversationId);
        }}
      />

      <ChatConfirmModal
        isOpen={archiving}
        confirmLabel={threadArchived ? 'Restore' : 'Archive'}
        message={
          threadArchived ? (
            <>
              Move{' '}
              <b>{selectedThread?.name ?? detailName ?? 'this conversation'}</b>{' '}
              back to the inbox?
            </>
          ) : (
            <>
              Archive{' '}
              <b>{selectedThread?.name ?? detailName ?? 'this conversation'}</b>
              ? It leaves the inbox for the Archived filter. Nobody is removed
              and nothing is deleted, and a new message brings it back into
              view.
            </>
          )
        }
        busy={archiveBusy}
        onClose={() => setArchiving(false)}
        onConfirm={toggleArchive}
      />

      <ConversationDetailsModal
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        conversationId={selectedId}
        onLeft={() => {
          selectThread(null);
          inbox.refresh();
        }}
      />
    </>
  );
};
